import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  useMediaQuery,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  SearchOutlined,
  GridViewOutlined,
  FactoryOutlined,
  BusinessOutlined,
  HandshakeOutlined,
  GroupsOutlined,
  AddOutlined,
  CloseOutlined,
  Check as CheckIcon,
  SearchOffOutlined,
} from "@mui/icons-material";
import { CompaniesQuery, CustomerCompany, CustomerCompanyState } from "../models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import ActionToast from "../../../shared/components/ActionToast";
import CompanyCard from "./CompanyCard";
import {
  getCompanies,
  changeQuery,
  getCategories,
  getManufacturers,
  getMachineCounts,
  setSuccess,
  setError,
} from "../customerCompaniesSlice";
import { getCustomer } from "../../settings/customerSettingsSlice";
import { CustomerSettingsState } from "../../settings/models/models";
import {
  getCompanyGroups,
  createCompanyGroup,
  deleteCompanyGroup,
  addCompanyGroupItem,
  deleteCompanyGroupItem,
} from "../../companyGroups/customerCompanyGroupsSlice";
import { CustomerCompanyGroupsState, CompanyGroup } from "../../companyGroups/models/models";
import Loader from "../../../shared/components/Loader";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import { NotificationMessage } from "../../../shared/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { RoleCode, CustomerTypeCode } from "../../shared/models/customerModels";
import { getPartnerships, getSentInvitations } from "../../partnerships/customerPartnershipsSlice";
import { CustomerPartnershipsState, RelationshipTierCode, InvitationStatusCode } from "../../partnerships/models/models";
import InvitePartnerDialog from "../../partnerships/components/InvitePartnerDialog";
import { dt as sharedDt, paginationWrapperSx } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

type FilterKey = "all" | "suppliers" | "buyers" | "partners" | string;

// =====================================================================
//  DESIGN TOKENS (extend shared tokens with page-specific colors)
// =====================================================================

const dt = {
  ...sharedDt,
  // Filter accent colors (alias shared tokens for local readability)
  green: sharedDt.roleGreen,
  purple: sharedDt.rolePurple,
  red: "#EF4444",
};

// =====================================================================
//  FilterItem -- clickable row for sidebar & accordion
// =====================================================================

interface FilterItemProps {
  label: string;
  count?: number;
  isSelected: boolean;
  accentColor?: string;
  icon?: React.ReactNode;
  indent?: boolean;
  dimmed?: boolean;
  onClick: () => void;
  rightAction?: React.ReactNode;
}

const FilterItem = ({ label, count, isSelected, accentColor, icon, indent, dimmed, onClick, rightAction }: FilterItemProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 10px",
      paddingLeft: indent ? "14px" : "10px",
      marginLeft: indent ? "8px" : 0,
      borderRadius: dt.radiusSm,
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: isSelected ? (accentColor ? `${accentColor}08` : dt.blueSoft) : "transparent",
      borderLeft: isSelected && accentColor ? `3px solid ${accentColor}` : "3px solid transparent",
      opacity: dimmed && !isSelected ? 0.65 : 1,
      "&:hover": {
        backgroundColor: isSelected ? (accentColor ? `${accentColor}10` : dt.blueGlow) : "#F8FAFC",
        opacity: 1,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontFamily: dt.font,
        fontWeight: isSelected ? 600 : 400,
        color: isSelected ? (accentColor || dt.blue) : dt.textSecondary,
        letterSpacing: "0.1px",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {icon && (
        <Box sx={{ display: "flex", flexShrink: 0, color: isSelected ? (accentColor || dt.blue) : dt.textMuted, transition: "color 0.2s" }}>
          {icon}
        </Box>
      )}
      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
      {count !== undefined && (
        <Box
          sx={{
            fontSize: "11px",
            fontFamily: dt.fontMono,
            fontWeight: 600,
            color: dt.textMuted,
            backgroundColor: dt.inputBg,
            padding: "2px 8px",
            borderRadius: "10px",
            minWidth: "24px",
            textAlign: "center",
            lineHeight: "16px",
          }}
        >
          {count}
        </Box>
      )}
      {rightAction}
    </Box>
  </Box>
);

// =====================================================================
//  PAGINATION WRAPPER
// =====================================================================


// =====================================================================
//  MAIN COMPONENT
// =====================================================================

const Companies = () => {
  const { translate } = useTranslation(["partnerHub", "common", "partnerships"]);
  const { query, companies, machineCounts, loading, success, error } =
    useAppSelector<CustomerCompanyState>((state: any) => state.customerCompanies);
  const { companyGroups } = useAppSelector<CustomerCompanyGroupsState>(
    (state: any) => state.customerCompanyGroups
  );
  const { customer: ownCustomer } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );
  const { partnerships, invitationsSent } = useAppSelector<CustomerPartnershipsState>(
    (state: any) => state.customerPartnerships
  );

  const dispatch = useAppDispatch();

  // 3-tier responsive: Desktop > 1300, Tablet+Phone <= 1300
  const isCompact = useMediaQuery("(max-width:1300px)");

  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Group management state
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState<string | null>(null);

  // Invite partner dialog: inviteTarget holds the company data (stable while
  // the dialog is visible), inviteDialogOpen drives the MUI Dialog open prop.
  // On open: set target THEN open. On close: close THEN clear target after
  // the exit animation via onExited. This avoids the flash caused by two
  // states being out of sync, while preserving the MUI exit animation.
  const [inviteTarget, setInviteTarget] = useState<CustomerCompany | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const groups: CompanyGroup[] = companyGroups?.items ?? [];

  // All bookmarked company IDs (in any group)
  const allGroupedCompanyIds = useMemo(() => {
    const ids = new Set<string>();
    groups.forEach((g) => {
      g.items?.forEach((item) => {
        if (item.customer?.id) ids.add(item.customer.id.toLowerCase());
      });
    });
    return ids;
  }, [groups]);

  // Active group company IDs (for group:xxx filter)
  const activeGroupCompanyIds = useMemo(() => {
    if (!activeFilter.startsWith("group:")) return null;
    const groupId = activeFilter.replace("group:", "");
    const group = groups.find((g) => g.id === groupId);
    if (!group?.items) return new Set<string>();
    return new Set(
      group.items.filter((item) => item.customer?.id).map((item) => item.customer!.id.toLowerCase())
    );
  }, [activeFilter, groups]);

  // Build lookup map: partnerId -> effectiveTier
  const partnerTierMap = useMemo(() => {
    const map = new Map<string, RelationshipTierCode>();
    if (!partnerships?.items || !ownCustomer?.id) return map;
    partnerships.items.forEach((p) => {
      const isA = ownCustomer.id.toLowerCase() === p.customerIdA.toLowerCase();
      const partnerId = isA ? p.customerIdB : p.customerIdA;
      map.set(partnerId.toLowerCase(), p.effectiveTier);
    });
    return map;
  }, [partnerships, ownCustomer]);

  // Build set of company IDs with pending sent invitations
  const pendingInviteeIds = useMemo(() => {
    const ids = new Set<string>();
    if (!invitationsSent?.items) return ids;
    invitationsSent.items.forEach((inv) => {
      if (inv.statusCode === InvitationStatusCode.Pending && inv.inviteeCustomerId) {
        ids.add(inv.inviteeCustomerId.toLowerCase());
      }
    });
    return ids;
  }, [invitationsSent]);

  // Unified company status map: companyId -> { type, tier }
  const companyStatusMap = useMemo(() => {
    const map = new Map<string, { type: "partner" | "pending" | "none"; tier?: RelationshipTierCode }>();
    if (!ownCustomer?.id) return map;
    // Add partners
    partnerTierMap.forEach((tier, companyId) => {
      map.set(companyId, { type: "partner", tier });
    });
    // Add pending (don't override partner status)
    pendingInviteeIds.forEach((companyId) => {
      if (!map.has(companyId)) {
        map.set(companyId, { type: "pending" });
      }
    });
    return map;
  }, [partnerTierMap, pendingInviteeIds, ownCustomer]);

  const groupsQuery = useMemo(
    () => ({ pageNo: 1, pageSize: 100, sortDesc: false }),
    []
  );

  // Shared animation keyframes (headerReveal, fadeUp, fadeLeft, cardReveal, etc.)
  useRedesignSetup();

  // Data loading
  useEffect(() => {
    dispatch(getCompanies(query));
    dispatch(getCategories());
    dispatch(getManufacturers());
    dispatch(getCompanyGroups(groupsQuery as any));
    dispatch(getCustomer());
    // Fetch partnerships for badge display on CompanyCards
    dispatch(getPartnerships({ pageNo: 1, pageSize: 200, sortDesc: true }));
    // Fetch sent invitations to detect pending state for cards
    dispatch(getSentInvitations({ pageNo: 1, pageSize: 200, sortDesc: true }));
  }, []);

  // Fetch machine counts when companies are loaded
  useEffect(() => {
    if (companies?.items?.length) {
      const ids = companies.items.map(c => c.id);
      dispatch(getMachineCounts(ids));
    }
  }, [companies?.items]);

  const reloadGroups = useCallback(() => {
    dispatch(getCompanyGroups(groupsQuery as any));
  }, [dispatch, groupsQuery]);

  // Toggle a company's membership in a specific group
  const handleToggleGroupMembership = useCallback(
    async (companyId: string, groupId: string, isInGroup: boolean) => {
      const group = groups.find((g) => g.id === groupId);
      if (isInGroup) {
        const item = group?.items?.find(
          (i) => i.customer?.id?.toLowerCase() === companyId.toLowerCase()
        );
        if (item) {
          await dispatch(deleteCompanyGroupItem({ id: groupId, itemId: item.id }));
        }
        dispatch(setSuccess(translate("partnerHub:groups.removedFromGroup", { name: group?.name || "" })));
      } else {
        await dispatch(addCompanyGroupItem({ id: groupId, customerId: companyId }));
        dispatch(setSuccess(translate("partnerHub:groups.addedToGroup", { name: group?.name || "" })));
      }
      reloadGroups();
    },
    [groups, dispatch, reloadGroups, translate]
  );

  // Create a new group and add a company to it
  const handleCreateGroupWithCompany = useCallback(
    async (name: string, companyId: string) => {
      await dispatch(createCompanyGroup({ name, customerIds: [companyId] }));
      dispatch(setSuccess(translate("partnerHub:groups.groupCreated", { name })));
      reloadGroups();
    },
    [dispatch, reloadGroups, translate]
  );

  // Create a new empty group (from sidebar)
  const handleCreateGroup = useCallback(
    async (name: string) => {
      await dispatch(createCompanyGroup({ name, customerIds: [] }));
      reloadGroups();
    },
    [dispatch, reloadGroups]
  );

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      await dispatch(deleteCompanyGroup({ id: groupId, model: {} }));
      if (activeFilter === `group:${groupId}`) setActiveFilter("all");
      setConfirmDeleteGroupId(null);
      reloadGroups();
    },
    [dispatch, reloadGroups, activeFilter]
  );

  // Invite partner handlers — set target first, then open in the same
  // batch so React renders once with both values correct.
  const handleInvitePartner = useCallback((company: CustomerCompany) => {
    setInviteTarget(company);
    setInviteDialogOpen(true);
  }, []);

  const handleInviteSuccess = useCallback((email?: string) => {
    // Show success toast
    dispatch(setSuccess(translate("partnerships:invite.successMessage", { email: email || "" })));
    // Re-fetch partnerships and sent invitations to refresh button states
    dispatch(getPartnerships({ pageNo: 1, pageSize: 200, sortDesc: true }));
    dispatch(getSentInvitations({ pageNo: 1, pageSize: 200, sortDesc: true }));
  }, [dispatch, translate]);

  // Filter handlers
  const handleFilterChange = (filter: FilterKey) => {
    setActiveFilter(filter);

    // Group and partners filters are client-side -- need full dataset (no roleCode)
    if (filter.startsWith("group:") || filter === "partners") {
      const newQuery: CompaniesQuery = { ...query, roleCode: undefined, pageNo: 1 };
      dispatch(changeQuery(newQuery));
      dispatch(getCompanies(newQuery));
      return;
    }

    let roleCode: RoleCode | undefined;
    // "suppliers" label = "Machine Operators" = Buyer role (they buy services)
    // "buyers" label = "Service Providers" = Supplier role (they supply services)
    if (filter === "suppliers") roleCode = RoleCode.buyer;
    else if (filter === "buyers") roleCode = RoleCode.supplier;

    const newQuery: CompaniesQuery = { ...query, roleCode, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(getCompanies(newQuery));
  };


  // Pagination handlers
  const handlePageNoChange = (newPage: number) => {
    const newQuery: CompaniesQuery = { ...query, pageNo: newPage };
    dispatch(changeQuery(newQuery));
    dispatch(getCompanies(newQuery));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newQuery: CompaniesQuery = { ...query, pageNo: 1, pageSize: newPageSize };
    dispatch(changeQuery(newQuery));
    dispatch(getCompanies(newQuery));
  };

  // Filtered companies (client-side for search + group + partners filtering)
  const filteredCompanies = useMemo(() => {
    if (!companies?.items) return [];
    let items = companies.items;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((c) => c.name.toLowerCase().includes(q));
    }

    if (activeGroupCompanyIds) {
      items = items.filter((c) => activeGroupCompanyIds.has(c.id.toLowerCase()));
    }

    if (activeFilter === "partners") {
      items = items.filter((c) => {
        const status = companyStatusMap.get(c.id.toLowerCase());
        return status?.type === "partner";
      });
    }

    return items;
  }, [companies?.items, searchQuery, activeGroupCompanyIds, activeFilter, companyStatusMap]);

  const isClientFiltered = searchQuery.trim() || activeGroupCompanyIds || activeFilter === "partners";
  const totalCount = isClientFiltered ? filteredCompanies.length : companies?.totalCount;

  // Count active sidebar filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (activeFilter !== "all") count++;
    if (searchQuery.trim()) count++;
    return count;
  }, [activeFilter, searchQuery]);

  // Cancel delete confirmation on outside click
  useEffect(() => {
    if (!confirmDeleteGroupId) return;
    const handleClick = () => setConfirmDeleteGroupId(null);
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [confirmDeleteGroupId]);

  // =====================================================================
  //  renderFilterContent -- shared between sidebar & accordion
  // =====================================================================

  const renderFilterContent = () => (
    <>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
          padding: "0 12px",
          height: "40px",
          transition: "all 0.25s ease",
          boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
          mb: "16px",
        }}
      >
        <SearchOutlined
          sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted, flexShrink: 0, transition: "color 0.2s" }}
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={translate("partnerHub:search.companies")}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "13px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "8px",
          }}
        />
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: `1px solid ${dt.cardBorder}`, mb: "16px" }} />

      {/* Filter header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px", px: "4px" }}>
        <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font }}>
          {translate("partnerHub:filters.companyType")}
        </Box>
        {activeFilter !== "all" && (
          <Box
            onClick={() => handleFilterChange("all")}
            sx={{ fontSize: "12px", color: dt.blue, cursor: "pointer", fontWeight: 500, fontFamily: dt.font, "&:hover": { textDecoration: "underline" } }}
          >
            {translate("common:buttons.clear")}
          </Box>
        )}
      </Box>

      {/* All companies */}
      <FilterItem
        label={translate("partnerHub:filters.all")}
        count={totalCount ?? 0}
        isSelected={activeFilter === "all"}
        accentColor={dt.blue}
        icon={<GridViewOutlined sx={{ fontSize: "15px" }} />}
        onClick={() => handleFilterChange("all")}
      />

      {/* Type filter chips — only shown for Hybrid (type 3) users.
          Non-hybrid users already see only the opposite type via backend auto-filter,
          so these chips would be redundant. */}
      {ownCustomer?.customerType === CustomerTypeCode.Both && (
        <>
          {/* Suppliers (Machine Operators) */}
          <FilterItem
            label={translate("partnerHub:filters.suppliers")}
            isSelected={activeFilter === "suppliers"}
            accentColor={dt.purple}
            icon={<FactoryOutlined sx={{ fontSize: "15px" }} />}
            indent
            onClick={() => handleFilterChange("suppliers")}
          />

          {/* Buyers (Service Providers) */}
          <FilterItem
            label={translate("partnerHub:filters.buyers")}
            isSelected={activeFilter === "buyers"}
            accentColor={dt.green}
            icon={<BusinessOutlined sx={{ fontSize: "15px" }} />}
            indent
            onClick={() => handleFilterChange("buyers")}
          />
        </>
      )}

      {/* Partners */}
      <FilterItem
        label={translate("partnerHub:filters.partners")}
        count={partnerTierMap.size}
        isSelected={activeFilter === "partners"}
        accentColor={dt.blue}
        icon={<HandshakeOutlined sx={{ fontSize: "15px" }} />}
        indent
        onClick={() => handleFilterChange("partners")}
      />

      {/* Groups section */}
      <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "10px", mx: "4px" }} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px", px: "4px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <GroupsOutlined sx={{ fontSize: "15px", color: dt.textMuted }} />
          <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font }}>
            {translate("partnerHub:groups.title")}
          </Box>
        </Box>
      </Box>

      {/* Group filter items */}
      {groups.map((group) => {
        const isActive = activeFilter === `group:${group.id}`;
        const isConfirming = confirmDeleteGroupId === group.id;
        return (
          <Box key={group.id} sx={{ position: "relative" }}>
            {isConfirming ? (
              <PermissionGuard module={Modules.PartnerHub} level={PermissionLevel.Admin} fallback={
                <FilterItem
                  label={group.name}
                  count={group.items?.length ?? 0}
                  isSelected={isActive}
                  accentColor={dt.amber}
                  indent
                  onClick={() => handleFilterChange(`group:${group.id}`)}
                />
              }>
                <Box
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "9px 10px",
                    paddingLeft: "14px",
                    marginLeft: "8px",
                    borderRadius: dt.radiusSm,
                    border: `1px solid ${dt.red}`,
                    backgroundColor: "#FEF2F2",
                  }}
                >
                  <Box sx={{ fontSize: "12px", color: dt.red, fontWeight: 600, fontFamily: dt.font }}>
                    {translate("partnerHub:groups.deleteGroup")}?
                  </Box>
                  <Box sx={{ display: "flex", gap: "6px" }}>
                    <Box
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                      sx={{
                        fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "4px",
                        cursor: "pointer", fontFamily: dt.font, color: "#fff", background: dt.red,
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      {translate("common:common.yes")}
                    </Box>
                    <Box
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteGroupId(null); }}
                      sx={{
                        fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "4px",
                        cursor: "pointer", fontFamily: dt.font, color: dt.textSecondary, border: `1px solid ${dt.cardBorder}`,
                        "&:hover": { borderColor: dt.textSecondary },
                      }}
                    >
                      {translate("common:common.no")}
                    </Box>
                  </Box>
                </Box>
              </PermissionGuard>
            ) : (
              <FilterItem
                label={group.name}
                count={group.items?.length ?? 0}
                isSelected={isActive}
                accentColor={dt.amber}
                indent
                onClick={() => handleFilterChange(`group:${group.id}`)}
                rightAction={
                  <PermissionGuard module={Modules.PartnerHub} level={PermissionLevel.Admin}>
                    <Box
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteGroupId(group.id); }}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: "16px", height: "16px", borderRadius: "50%",
                        color: dt.textMuted, fontSize: "10px", cursor: "pointer",
                        transition: "all 0.2s", opacity: 0,
                        ".MuiBox-root:hover > .MuiBox-root > .MuiBox-root > &, &:hover": { opacity: 1, color: dt.red },
                      }}
                    >
                      <CloseOutlined sx={{ fontSize: 11 }} />
                    </Box>
                  </PermissionGuard>
                }
              />
            )}
          </Box>
        );
      })}

      {/* Add new group */}
      {!showNewGroupInput ? (
        <Box
          onClick={() => setShowNewGroupInput(true)}
          sx={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "9px 10px", paddingLeft: "22px",
            borderRadius: dt.radiusSm,
            color: dt.blue, fontSize: "13px", fontWeight: 500, cursor: "pointer",
            fontFamily: dt.font, transition: "all 0.2s",
            "&:hover": { backgroundColor: "#F8FAFC" },
          }}
        >
          <AddOutlined sx={{ fontSize: 14 }} />
          {translate("partnerHub:groups.newGroup")}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: "4px",
            padding: "4px 10px", paddingLeft: "22px",
          }}
        >
          <input
            autoFocus
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newGroupName.trim()) {
                handleCreateGroup(newGroupName.trim());
                setNewGroupName("");
                setShowNewGroupInput(false);
              }
              if (e.key === "Escape") { setShowNewGroupInput(false); setNewGroupName(""); }
            }}
            placeholder={translate("partnerHub:groups.newGroup")}
            style={{
              flex: 1, border: `1px solid ${dt.cardBorder}`, borderRadius: "6px",
              padding: "5px 8px", fontSize: "12px", fontFamily: 'var(--font-sans)',
              color: dt.textPrimary, outline: "none", background: "#F8FAFC",
            }}
          />
          <Box
            onClick={() => {
              if (newGroupName.trim()) {
                handleCreateGroup(newGroupName.trim());
                setNewGroupName("");
                setShowNewGroupInput(false);
              }
            }}
            sx={{
              width: 26, height: 26, borderRadius: "6px",
              background: dt.blue, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            <CheckIcon sx={{ fontSize: 12 }} />
          </Box>
        </Box>
      )}
    </>
  );

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        overflow: "hidden",
        px: { xs: "16px", md: "32px" },
      }}
    >
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />


      {/* =====================================================================
          MAIN CONTENT
          ===================================================================== */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          pt: { xs: 3, md: 4 },
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* =================================================================
            DESKTOP SIDEBAR (>1200px only)
            ================================================================= */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              overflowY: "auto",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* =================================================================
            MAIN AREA (accordion filters + card grid — SCROLLS HERE)
            ================================================================= */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflowY: "auto",
            pb: 3,
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            /* Styled scrollbar */
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.15)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.25)" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.15) transparent",
          }}
        >
          {/* =============================================================
              ACCORDION FILTERS (tablet & phone -- compact mode)
              ============================================================= */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("partnerHub:search.companies")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* =============================================================
              CARD GRID
              ============================================================= */}
          {(loading || !ownCustomer) ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <Loader />
            </Box>
          ) : filteredCompanies.length === 0 ? (
            <Box sx={{ textAlign: "center", py: "60px" }}>
              <SearchOffOutlined sx={{ fontSize: 40, color: dt.textMuted }} />
              <Box sx={{ fontSize: "15px", color: dt.textSecondary, mt: 1.5, fontFamily: dt.font }}>
                {translate("partnerHub:empty.noCompaniesFound")}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? { xs: "1fr", sm: "repeat(2, 1fr)" }
                  : "repeat(3, 1fr)",
                gap: { xs: "16px", md: "20px" },
              }}
            >
              {filteredCompanies.map((company, idx) => {
                const isOwnCompany = ownCustomer?.id?.toLowerCase() === company.id?.toLowerCase();
                const cIdLower = company.id.toLowerCase();
                const status = companyStatusMap.get(cIdLower);
                // Build partnershipStatus: partner/pending from map, or "none" for non-own companies
                const partnershipStatus = status
                  ? status
                  : isOwnCompany
                    ? undefined
                    : { type: "none" as const };
                return (
                  <Box key={company.id}>
                    <CompanyCard
                      company={company}
                      index={idx}
                      isOwnCompany={isOwnCompany}
                      isBookmarked={isOwnCompany ? false : allGroupedCompanyIds.has(cIdLower)}
                      groups={isOwnCompany ? undefined : groups}
                      onToggleGroup={isOwnCompany ? undefined : handleToggleGroupMembership}
                      onCreateGroupWithCompany={isOwnCompany ? undefined : handleCreateGroupWithCompany}
                      partnershipTier={partnerTierMap.get(cIdLower)}
                      partnershipStatus={partnershipStatus}
                      onInvitePartner={isOwnCompany ? undefined : handleInvitePartner}
                      machineCount={machineCounts[company.id]}
                    />
                  </Box>
                );
              })}
            </Box>
          )}

          {/* =============================================================
              PAGINATION
              ============================================================= */}
          {!isClientFiltered && companies && companies.totalCount > 0 && (
            <Box
              sx={{
                mt: "8px",
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: dt.cardShadow,
                "& > div": {
                  borderTop: "none",
                  borderRadius: dt.radiusSm,
                  padding: "6px 18px",
                  backgroundColor: dt.cardBg,
                },
                ...paginationWrapperSx,
              }}
            >
              <LightTablePagination
                totalCount={companies.totalCount}
                currentPage={query.pageNo}
                pageSize={query.pageSize}
                pageSizeOptions={[10, 20, 50]}
                onPageChange={handlePageNoChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageLabel={translate("common:rowsPerPage")}
                ofLabel={translate("common:of")}
              />
            </Box>
          )}

        </Box>
      </Box>

      {/* Invite Partner Dialog — conditionally rendered so it only mounts
           after inviteTarget is set, preventing a flash where mode="full"
           shows briefly before switching to mode="profile".
           onClose only sets open=false; target is cleared after the MUI
           exit animation via TransitionProps.onExited to keep props stable. */}
      {inviteTarget && (
        <InvitePartnerDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          onSuccess={(email?: string) => {
            setInviteDialogOpen(false);
            handleInviteSuccess(email);
          }}
          prefillCompanyName={inviteTarget.name}
          prefillEmail={inviteTarget.email || undefined}
          prefillCustomerType={inviteTarget.customerType || undefined}
          viewerCustomerType={ownCustomer?.customerType}
          mode="profile"
        />
      )}
    </Box>
  );
};

export default Companies;
